import {
  Box,
  HStack,
  Image,
  Skeleton,
  Text,
  useRadio,
  useRadioGroup,
  UseRadioGroupProps,
  UseRadioProps,
} from "@chakra-ui/react";
import { capitalize } from "lodash";

export interface ImageSelectFieldProps {
  images: ImageItem[];
  loading?: boolean;
  error?: string;
}

interface ImageItem {
  image: string;
  title: string;
  value: string;
}

function ImageLoadingCardsSkeleton() {
  return (
    <HStack>
      <Skeleton w="7rem" h="4rem" />
      <Skeleton w="7rem" h="4rem" />
      <Skeleton w="7rem" h="4rem" />
    </HStack>
  );
}

function ImageCard({
  image,
  title,
  isError,
  ...props
}: Omit<ImageItem, "value"> &
  UseRadioProps & {
    isError?: boolean;
  }) {
  const {
    getInputProps,
    getRadioProps,
    getLabelProps,
    htmlProps,
    state: { isChecked, isDisabled, isFocused },
  } = useRadio(props);

  return (
    <Box
      as="label"
      {...htmlProps}
      cursor={isDisabled ? "not-allowed" : "pointer"}
    >
      <input {...getInputProps({})} hidden />
      <Box
        {...getRadioProps()}
        position="relative"
        ring={isFocused ? "2px solid" : "none"}
        border="2px solid"
        opacity={isDisabled ? 0.5 : 1}
        borderColor={
          isError ? "danger.500" : isChecked ? "primary.500" : "blackAlpha.300"
        }
        p="0.5rem"
        transition="box-shadow 0.5s ease-in-out"
        _hover={{
          boxShadow: "lg",
          dropShadow: "md",
        }}
        w="9.5rem"
        h="4.75rem"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        borderRadius="md"
      >
        <Image
          maxW="7rem"
          maxH="3.75rem"
          objectPosition="center center"
          objectFit="cover"
          src={image}
          alt={title}
          fallback={
            <Box
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: isChecked ? "primary.500" : "surface.700",
              }}
            >
              {capitalize(title?.split("_")[0]) || "Payment Method"}
            </Box>
          }
          {...getLabelProps()}
        />
      </Box>
    </Box>
  );
}

export function ImageSelectField({
  images,
  loading = false,
  error,
  ...props
}: UseRadioGroupProps & ImageSelectFieldProps) {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "image",
    onChange: console.log,
    ...props,
  });

  const isError = Boolean(error);
  if (loading) return <ImageLoadingCardsSkeleton />;

  return (
    <>
      <HStack {...getRootProps()} spacing="1rem">
        {images.map((image) => {
          const { value, image: imageSrc, title } = image;
          return (
            <ImageCard
              isError={isError}
              key={`image_selector_field_${value}`}
              image={imageSrc}
              title={title}
              {...getRadioProps({ value })}
            />
          );
        })}
      </HStack>
      {isError && (
        <Text mt="0.5rem" color="danger.500">
          {error}
        </Text>
      )}
    </>
  );
}
